/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import moment from "moment";
import startCase from "lodash/startCase";
import upperFirst from "lodash/upperFirst";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";

import {
  getMedicineMultiplier,
  showFrequencyUnit,
  showMeal,
  showTimeUnit
} from "../../Medication/Medication";
import PrintRow from "../PrintRow";
import { t } from "../../../../components/translate";
import { VITALS_SHORT_NAME } from "../PrintComponents";
import { countableTypes } from "../../Medication/medicineType";
import { TableState } from "../../../../components/EditableTable/EditableTable";
import { getFollowupFormat } from "../../../Modals/Assessment/AssessmentContent";
import { convertADtoBS } from "../../../../components/Calendar/functions/calendarFunctions";
import {
  DentalService,
  Diagnosis,
  ExtraOral,
  Followup,
  IntraOralValue,
  Medicine,
  OtherService,
  Questionnaire,
  SkinAnalysisAttribute,
  TreatmentDoneItem,
  IVitals,
  XrayService
} from "../../../../interfaces/AssessmentInterfaces";
import EyeServices from "../../EyeServices";

export function TextContent({
  value,
  label = "Label"
}: {
  value: string;
  label: string;
}): React.ReactNode {
  return (
    <PrintRow label={label} labelWidth={160}>
      <Typography>{value}</Typography>
    </PrintRow>
  );
}

export function VitalsContent({ vitals }: { vitals: Array<IVitals> }): React.ReactNode {
  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 2
      }}
    >
      {vitals.map((v) => (
        <Box
          key={`${v.name}-${v.reading}-${v.unit}`}
          sx={{
            px: 1.25,
            py: 0.5,
            backgroundColor: "#f9f9f9",
            border: "1px solid #f0f0f0",
            borderRadius: "5px"
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {VITALS_SHORT_NAME[v.name as keyof typeof VITALS_SHORT_NAME]}
          </Typography>
          <Typography variant="body1">
            {v.reading} {v.unit}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function QuestionnaireContent({
  questionnaire
}: {
  questionnaire: Array<Questionnaire>;
}): React.ReactNode {
  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 2
      }}
    >
      {questionnaire.map((q) => (
        <Box
          key={`${q.name}-${q.reading}`}
          sx={{
            px: 1.25,
            py: 0.5,
            backgroundColor: "#f9f9f9",
            border: "1px solid #f0f0f0",
            borderRadius: "5px"
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {q.name}
          </Typography>
          <Typography variant="body1">{q.reading}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export function OtherServiceContent({
  services = []
}: {
  services: Array<OtherService>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.map((s, index) => (
            <TableRow key={s.id || `service-${index}`}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{s.name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function DiagnosisContent({
  diagnosis = []
}: {
  diagnosis: Array<Diagnosis>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>ICD Code</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Diagnosis</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {diagnosis.map((d, index) => (
            <TableRow key={`${d.icdCode}-${d.diagnosis}`}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{d.icdCode}</TableCell>
              <TableCell>{d.diagnosis}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function XRayServiceContent({
  services = []
}: {
  services: Array<XrayService>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Teeth</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.map((s, index) => (
            <TableRow key={s.id || s.name}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.teeth?.join(", ")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function MedicationContent({
  medications = []
}: {
  medications: Array<Medicine>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Medicine</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Instruction</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Remarks</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {medications.map((m, index) => (
            <React.Fragment key={m.id || `med-${index}`}>
              <TableRow>
                <TableCell sx={{ verticalAlign: "top" }}>{index + 1}.</TableCell>

                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {`${m.genericName || ""}${m.genericName && m.brand ? " / " : ""}${
                      m.brand || ""
                    }`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${m.strength || ""} ${m.form ? `(${m.form})` : ""}`}
                  </Typography>
                </TableCell>

                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography variant="body2">
                    {m.frequency === 0 ? "" : `${m.frequency} ${t("assessment.times")} `}
                    {m.frequency !== 0 && showTimeUnit(m.times)}
                    {m.meal ? ` ${showMeal(m.meal)}` : ""}
                  </Typography>
                  {Array.isArray(m.partsOfDay) && m.partsOfDay.length > 0 && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                      <b>{t("assessment.medication.partsOfDay") || "Part of the Day"}:</b>{" "}
                      {m.partsOfDay.map((tod) => t(tod) || tod).join(", ")}
                    </Typography>
                  )}
                  {Array.isArray(m.timesOfDay) && m.timesOfDay.length > 0 && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                      <b>{t("assessment.medication.timesOfDay") || "Time"}:</b>{" "}
                      {m.timesOfDay.join(", ")}
                    </Typography>
                  )}
                </TableCell>

                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography variant="body2">
                    {`${m.duration} ${showFrequencyUnit(m.frequencyType, m.duration)}`}
                  </Typography>
                </TableCell>

                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography variant="body2">
                    {countableTypes.includes(m.form)
                      ? m.frequency * getMedicineMultiplier(m.times, m.duration, m.frequencyType)
                      : ""}
                    {m.form}
                  </Typography>
                </TableCell>

                <TableCell sx={{ verticalAlign: "top" }}>
                  <Typography variant="caption">{m.remark || ""}</Typography>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function IntraOralContent({
  intraOralDoc
}: {
  intraOralDoc: Record<string, Record<string, IntraOralValue>>;
}): React.ReactNode {
  const oralTypes = ["permanent", "primary"];

  const quadrantMapper: Record<string, Record<string, string>> = {
    permanent: {
      1: t("history.upperRight"),
      2: t("history.upperLeft"),
      3: t("history.lowerLeft"),
      4: t("history.lowerRight")
    },
    primary: {
      5: t("history.upperRight"),
      6: t("history.upperLeft"),
      7: t("history.lowerLeft"),
      8: t("history.lowerRight")
    }
  };

  const intraOralDataDisplay = (data: string, item: string, type: string) => {
    let st: string = upperFirst(data);
    const toothData = intraOralDoc[type][item];

    if (data !== "impacted" && data !== "missing" && data !== "others") {
      if (data === "decayed") {
        const decayedValues = toothData.decayed?.values;
        if (decayedValues) {
          const firstKey = Object.keys(decayedValues)[0];
          if (firstKey) {
            st = startCase(firstKey);
            if (firstKey === "caries") {
              const level = decayedValues.caries?.level;
              if (level) {
                st += `(Class ${level})`;
              }
            }
          }
        }
      } else if (data === "mobile") {
        const level = toothData.mobile?.level;
        if (level) {
          st += `(Class ${level})`;
        }
      } else if (data === "fractured") {
        const level = toothData.fractured?.level;
        if (level) {
          st += `(Class ${level})`;
        }
      }
    }
    return st;
  };

  return (
    <Box sx={{ width: "100%" }}>
      {oralTypes.map((type) => (
        <React.Fragment key={type}>
          {Object.keys(intraOralDoc[type]).length !== 0 && (
            <Box sx={{ width: "100%", mb: 2 }}>
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    border: 1,
                    borderColor: (theme) => theme.palette.divider,
                    borderRadius: "5px"
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" colSpan={4}>
                        {type === "permanent" ? t("assessment.permanent") : t("assessment.primary")}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 140 }}>Quadrant</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 60 }}>Tooth</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Findings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(intraOralDoc[type]).map((item, index) => (
                      <TableRow key={item}>
                        <TableCell>{index + 1}.</TableCell>
                        <TableCell>{quadrantMapper[type][item.charAt(0)]}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{item}</TableCell>
                        <TableCell>
                          {Object.keys(intraOralDoc[type][item])
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            .filter((val) => (intraOralDoc[type][item] as any)[val]?.checked)
                            .map((data) => intraOralDataDisplay(data, item, type))
                            .join(", ")}
                          {intraOralDoc[type][item].others &&
                            ` - ${intraOralDoc[type][item].others}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

export function ExtraOralContent({ extraOral }: { extraOral: ExtraOral }): React.ReactNode {
  const fields = [
    { key: "tmz", label: "TMZ" },
    { key: "lymphNodes", label: "Lymph Nodes" },
    { key: "otherFindings", label: "Other Findings" }
  ];

  const data = fields.filter((f) => !!extraOral[f.key as keyof ExtraOral]);

  if (data.length === 0) return null;

  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600, width: 150 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Reading</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((f, index) => (
            <TableRow key={f.key}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>{f.label}</TableCell>
              <TableCell>{extraOral[f.key as keyof ExtraOral]}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function TabularDataContent({ tableState }: { tableState: TableState }): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableBody>
          {tableState.nodes.map((row, idx) => (
            <TableRow key={`row-${row[0]?.y ?? idx}`}>
              {row.map((node) => (
                <TableCell
                  key={`cell-${node.x}-${node.y}`}
                  sx={{
                    fontWeight: idx === 0 ? 600 : 400,
                    backgroundColor: idx === 0 ? "action.hover" : "inherit"
                  }}
                >
                  {node.data}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function DentalServiceContent({
  services = []
}: {
  services: Array<DentalService>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Teeth</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.map((s, index) => (
            <TableRow key={s.id || s.name}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.teeth?.join(", ")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function EyeServiceContent({
  services = []
}: {
  services: Array<EyeServices>;
}): React.JSX.Element {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.map((s, index) => (
            <TableRow key={s.id || s.name}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{s.name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function TreatmentDoneContent({
  treatmentDone = []
}: {
  treatmentDone: Array<TreatmentDoneItem>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {treatmentDone
            .filter((item) => item.checked)
            .map((item, index) => (
              <TableRow key={item.service.id}>
                <TableCell sx={{ verticalAlign: "top" }}>{index + 1}.</TableCell>
                <TableCell>
                  <Typography variant="body2">{item.service.name}</Typography>
                  {/* TODO: Isn't document an object? When does it have length? */}
                  {(item.service as any).document?.length > 0 && (
                    <Box pl={2}>
                      {(item.service as any).document
                        .filter((sub: any) => sub.checked)
                        .map((sub: any, subIndex: number) => (
                          <Typography
                            key={sub.service.id || subIndex}
                            variant="body2"
                            color="text.secondary"
                          >
                            - {sub.service.name}
                          </Typography>
                        ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>{item?.service?.note || ""}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function FollowUpContent({ followup }: { followup: Followup }): React.ReactNode {
  return (
    <Box>
      {followup?.days && (
        <Typography component="span">{`Follow up in ${followup.days} ${getFollowupFormat(
          followup
        )} ${followup.note}`}</Typography>
      )}

      {followup?.date && (
        <>
          <Typography>
            {`Follow up on ${convertADtoBS(moment(followup.date).toDate()).formatted2} (${moment(
              followup.date
            ).format("YYYY-MM-DD")} AD) ${
              followup.startDate
                ? ` from ${moment(followup.startDate).format("h:mm A")} to ${moment(
                    followup.endDate
                  ).format("h:mm A")}`
                : ""
            }`}
          </Typography>

          {followup?.note && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{followup.note}</Typography>
          )}
        </>
      )}
    </Box>
  );
}

export function LabTestContent({
  tests = []
}: {
  tests: Array<{ id?: number | string; name: string }>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tests.map((testItem, index) => (
            <TableRow key={testItem.id || index}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell>{testItem.name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function SkinAnalysisContent({
  skinAnalysis = []
}: {
  skinAnalysis: Array<SkinAnalysisAttribute>;
}): React.ReactNode {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ border: 1, borderColor: (theme) => theme.palette.divider, borderRadius: "5px" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>S.N.</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Reading</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {skinAnalysis.map((s, index) => (
            <TableRow key={s.dbKey}>
              <TableCell>{index + 1}.</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>{s.name}</TableCell>
              <TableCell>{s.reading}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
